import { Button, Chip, Icon, TextField } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useBulletinBoardCommentMutation, useBulletinBoardThreadMutation } from 'api/bulletinBoard/bulletinBoard.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'

import styles from './TextField.module.less'

type BulletinBoardTextFieldProps = {
  ids: number[]
  type: `thread` | `comment`
  content: number
}

export default function BulletinBoardTextField({ ids, type }: BulletinBoardTextFieldProps): ReactElement {
  const { t } = useTranslation()

  const [text, setText] = useState(``)
  const [files, setFiles] = useState<File[] | undefined>()
  const [commentTextCursorIndex, setCommentTextCursorIndex] = useState(-1)

  const { systemId } = useAuth()
  const { mutate: bulletinBoardThreadPost } = useBulletinBoardThreadMutation()
  const { mutate: bulletinBoardCommentPost } = useBulletinBoardCommentMutation()

  function post(): void {
    const filesToPost = files
    setFiles(undefined)
    setText(``)
    if (type === `comment`) {
      bulletinBoardCommentPost({ bulletin_board_thread: ids[0], content: text, files: filesToPost })
    } else {
      if (systemId) {
        bulletinBoardThreadPost({ system: systemId, content: text, files: filesToPost })
      }
      if (ids.length > 0) {
        ids.forEach(function (receiverId) {
          bulletinBoardThreadPost({ system: receiverId, content: text, files: filesToPost })
        })
      }
    }
  }

  function textfieldCursorChange(e: React.SyntheticEvent): void {
    const target = e.target as HTMLInputElement
    if (
      target.selectionStart !== undefined &&
      target.selectionStart !== null &&
      target.selectionStart !== commentTextCursorIndex
    ) {
      setCommentTextCursorIndex(target.selectionStart)
    }
  }

  function keyPress(e: React.KeyboardEvent): void {
    const target = e.target as HTMLTextAreaElement
    if (e.ctrlKey && e.keyCode === 13 && target.value?.trim().length > 0) {
      post()
    }
  }

  const hasChangeCommentsAccess = useHasPermission('change_comments')

  return (
    <>
      <Grid container sx={{ width: '100%' }} direction="row" columnSpacing={1} alignItems="center" wrap="nowrap">
        <Grid size={{ xs: 12 }} className={type === `comment` ? undefined : styles.TextField_TextFieldThread}>
          <TextField
            minRows={1}
            variant="standard"
            maxRows={10}
            value={text}
            aria-label="Post text field"
            onFocus={textfieldCursorChange}
            onKeyUp={(event) => {
              textfieldCursorChange(event)
              keyPress(event)
            }}
            onClick={textfieldCursorChange}
            onChange={(e) => {
              textfieldCursorChange(e)
              setText(e.target.value)
            }}
            placeholder={type === `comment` ? t(`Write your comment here..`) : t(`Write your message here..`)}
            multiline
            fullWidth
            className={styles.TextField_TextField}
            slotProps={{
              input: {
                disableUnderline: true,
              },
            }}
          />
          {files
            ? files.map((file) => (
                <Chip
                  key={file.name}
                  size="small"
                  icon={<Icon className={`fal fa-file`} />}
                  onDelete={() => setFiles(undefined)}
                  label={file.name}
                  className={styles.TextField_UploadFileIcon}
                />
              ))
            : null}
        </Grid>
        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => post()}
            className={type === `comment` ? styles.TextField_SendButtonComment : styles.TextField_SendButtonThread}
            disabled={(text.trim().length <= 0 ? true : false) || (!hasChangeCommentsAccess ? true : false)}
            sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
          >
            {type === `comment` ? t(`Send`) : t(`Post`)}
          </Button>
        </Grid>
      </Grid>
    </>
  )
}
